/**
 * Cost Analysis Tab — Three-bucket cost breakdown with escalation timeline.
 *
 * Buckets:
 * 1. New Well Installation (capital) — from sentinel well recommendations
 * 2. Active Remediation (operating) — from declared systems x rate catalog
 * 3. O&M / Monitoring (operating) — sampling + reporting + mobilization
 *
 * Lazy-fetches full projection data for the escalation timeline table.
 * Cost buckets are fetched from /cost-buckets endpoint.
 */
import { useEffect, useState, useCallback } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { FadeIn } from '../animations/FadeIn';
import { RemediationSystemsPanel } from './RemediationSystemsPanel';
import {
  fetchCostProjectionSnapshot,
  fetchCostBuckets,
  fetchV32CostCurve,
} from '../../api/polygon';
import type {
  CostProjectionSnapshotResponse,
  CostProjectionMonthly,
  CostBuckets,
  V32CostCurveResponse,
} from '../../api/polygon';

// QC well patterns to filter from sentinel display (matches backend)
const QC_WELL_PATTERNS = ['Dup', 'Blank', 'Composite', 'Stockpile', 'Duplicate'];

function isRealWellName(name: string | null | undefined): boolean {
  if (!name) return true;
  return !QC_WELL_PATTERNS.some(p => name.includes(p));
}

function formatCurrency(value: number): string {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${Math.round(value / 1000).toLocaleString()}K`;
  return `$${value.toFixed(0)}`;
}

function formatCurrencyFull(value: number): string {
  return `$${Math.round(value).toLocaleString()}`;
}

function techLabel(tech: string): string {
  const labels: Record<string, string> = {
    mdpe: 'MDPE', solar_skimmer: 'Solar Skimmer', pump_and_treat: 'Pump & Treat',
    air_sparging: 'Air Sparging', sve: 'SVE', isco: 'ISCO',
    bioremediation: 'Bioremediation', mna: 'MNA',
  };
  return labels[tech] || tech.replace(/_/g, ' ');
}

interface CostAnalysisTabProps {
  siteId: string;
  costSnapshot: CostProjectionSnapshotResponse | null;
  hasPrecomputedCosts: boolean;
  cost24mo: number;
  totalProjectionMonths: number;
  /** Recommended delineation-well count surfaced from the 2D map (sentinel alerts).
   *  When provided, overrides the snapshot's well count so the cost figure
   *  matches what the user sees on the map. */
  recommendedDelineationWellCount?: number;
  /** Average total_depth_ft of existing wells. When provided, used to derive
   *  per-well install cost from actual site depths instead of the cached tier. */
  avgExistingWellDepthFt?: number;
}

// Frontend depth-tiered pricing for new monitoring well installation.
// Tunable; intentionally crude — closely enough mirrors typical TX env costs.
function depthBasedCostPerWell(avgDepthFt: number): number {
  if (avgDepthFt <= 30) return 12_000;
  if (avgDepthFt <= 100) return 25_000;
  if (avgDepthFt <= 200) return 45_000;
  if (avgDepthFt <= 350) return 70_000;
  return 100_000;
}

function deriveAnnualOm(mp: CostProjectionMonthly | null) {
  if (!mp) return { monitoring: 0, regulatory: 0, remediation: 0, total: 0 };
  const monitoring = (mp.monitoring_cost || 0) / 5;
  const regulatory = (mp.regulatory_cost || 0) / 5;
  const remediation = (mp.base_remediation_cost || 0) / 5;
  return {
    monitoring,
    regulatory,
    remediation,
    total: monitoring + regulatory + remediation,
  };
}

export function CostAnalysisTab({
  siteId,
  costSnapshot,
  hasPrecomputedCosts,
  cost24mo,
  totalProjectionMonths,
  recommendedDelineationWellCount,
  avgExistingWellDepthFt,
}: CostAnalysisTabProps) {
  // Lazy-fetch full projection when tab mounts (bundle only has scalar fields)
  const [fullSnapshot, setFullSnapshot] = useState<CostProjectionSnapshotResponse | null>(null);
  const [loadingFull, setLoadingFull] = useState(false);

  // Three cost buckets
  const [buckets, setBuckets] = useState<CostBuckets | null>(null);
  const [loadingBuckets, setLoadingBuckets] = useState(false);

  // v3.2 cost curve — drives the "Recommended Remediation @ 20yr" block by
  // pulling per-well long-horizon exceedance flags from the engine.
  const [v32Cost, setV32Cost] = useState<V32CostCurveResponse | null>(null);
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    fetchV32CostCurve(siteId)
      .then(resp => { if (!cancelled) setV32Cost(resp); })
      .catch(err => {
        if (cancelled) return;
        console.warn('[POLYGON] v3.2 cost curve fetch failed in CostAnalysisTab:', err);
      });
    return () => { cancelled = true; };
  }, [siteId]);

  const loadBuckets = useCallback(async (fresh = false) => {
    if (!siteId) return;
    setLoadingBuckets(true);
    try {
      const data = await fetchCostBuckets(siteId, fresh);
      setBuckets(data);
    } catch (err) {
      console.error('Cost buckets fetch failed:', err);
    } finally {
      setLoadingBuckets(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (!siteId) return;

    // Fetch full projection for escalation timeline + embedded cost_buckets
    if (hasPrecomputedCosts && !fullSnapshot) {
      setLoadingFull(true);
      setLoadingBuckets(true);
      fetchCostProjectionSnapshot(siteId)
        .then(snap => {
          setFullSnapshot(snap);
          // Paint embedded cost_buckets from the snapshot for an instant
          // render. The snapshot is a periodic pre-computation, so its
          // active_remediation totals can lag behind live RemediationSystem
          // rows — fire loadBuckets() right after to reconcile.
          const embedded = snap?.projection?.cost_buckets;
          if (embedded) {
            setBuckets(embedded);
            // Refresh in background so the top card matches the live system
            // list rendered by RemediationSystemsPanel.
            loadBuckets();
          } else {
            // Snapshot exists but no embedded buckets — fetch separately
            loadBuckets();
            return;
          }
        })
        .catch(err => {
          console.error('Cost snapshot fetch failed:', err);
          // Fallback to separate bucket fetch
          loadBuckets();
          return;
        })
        .finally(() => {
          setLoadingFull(false);
          setLoadingBuckets(false);
        });
    } else {
      // No precomputed snapshot — fetch buckets directly
      loadBuckets();
    }
  }, [siteId, hasPrecomputedCosts]);

  const effectiveSnapshot = fullSnapshot || costSnapshot;
  const proj = effectiveSnapshot?.projection;
  const monthlyProjections = proj?.monthly_projections || [];
  const wellCount = costSnapshot?.well_count || buckets?.om_monitoring.well_count || 0;

  // Show quarterly if many rows
  const timelineRows = monthlyProjections.length > 12
    ? monthlyProjections.filter((mp: CostProjectionMonthly) => mp.month % 3 === 0 || mp.month === 1)
    : monthlyProjections;

  // Filter sentinel wells from full projection
  const sentinelWellsFromProj = (proj?.sentinel_wells || []).filter(sw => isRealWellName(sw.trigger_well));
  // Sentinel wells from buckets (more authoritative when available)
  const sentinelWellsFromBuckets = (buckets?.new_well_installation.sentinel_wells || []).filter(sw => isRealWellName(sw.trigger_well));
  const sentinelWells = sentinelWellsFromBuckets.length > 0 ? sentinelWellsFromBuckets : sentinelWellsFromProj;

  return (
    <FadeIn delay={200}>
      <div className="space-y-6">
        {/* ── Three Cost Buckets ── */}
        {(buckets || loadingBuckets) && (
          <div className="grid grid-cols-3 gap-4">
            {/* BUCKET 1: New Well Installation (Capital) */}
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-5">
              <h4 className="text-xs font-semibold text-mono-500 tracking-wider mb-4">NEW WELL INSTALLATION</h4>
              {loadingBuckets ? (
                <div className="h-20 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : buckets ? (() => {
                // Override count + depth from props when supplied. These come
                // from the 2D map (sentinel alerts) and the extracted-data
                // average so the cost line stays consistent with what the
                // user sees on the map and the actual site depths.
                const overrideCount = recommendedDelineationWellCount;
                const overrideDepth = avgExistingWellDepthFt;
                const wellCount = overrideCount != null ? overrideCount : sentinelWells.length;
                const displayDepth = overrideDepth != null
                  ? Math.round(overrideDepth)
                  : buckets.new_well_installation.avg_depth_ft;
                const perWellCost = overrideDepth != null
                  ? depthBasedCostPerWell(overrideDepth)
                  : buckets.new_well_installation.cost_per_well;
                const totalCost = wellCount * perWellCost;
                return (
                  <div className="space-y-3">
                    <div>
                      <p className="text-mono-100 text-2xl font-bold font-orbitron">
                        {formatCurrencyFull(totalCost)}
                      </p>
                      <p className="text-mono-600 text-[10px] tracking-wider mt-1">CAPITAL (ONE-TIME)</p>
                    </div>
                    <div className="pt-3 border-t border-mono-800 space-y-1.5">
                      <div className="flex justify-between">
                        <span className="text-mono-400 text-xs">Recommended Wells {overrideCount != null ? '(from 2D map)' : ''}</span>
                        <span className="text-mono-200 text-xs font-semibold">{wellCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-mono-400 text-xs">
                          Cost per Well{displayDepth ? ` (avg. ${displayDepth} ft)` : ''}
                        </span>
                        <span className="text-mono-200 text-xs font-semibold">{formatCurrencyFull(perWellCost)}</span>
                      </div>
                    </div>
                  </div>
                );
              })() : null}
            </div>

            {/* BUCKET 2: Active Remediation (Operating) */}
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-5">
              <h4 className="text-xs font-semibold text-mono-500 tracking-wider mb-4">ACTIVE REMEDIATION</h4>
              {loadingBuckets ? (
                <div className="h-20 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : buckets ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-mono-100 text-2xl font-bold font-orbitron">
                      {formatCurrencyFull(buckets.active_remediation.monthly_total)}<span className="text-mono-500 text-sm font-normal">/mo</span>
                    </p>
                    <p className="text-mono-600 text-[10px] tracking-wider mt-1">
                      {formatCurrencyFull(buckets.active_remediation.annual_total)}/YR
                    </p>
                  </div>
                  {buckets.active_remediation.systems.length > 0 ? (
                    <div className="pt-3 border-t border-mono-800 space-y-1.5">
                      {buckets.active_remediation.systems.map(sys => (
                        <div key={sys.system_id} className="flex justify-between">
                          <span className="text-mono-400 text-xs">{techLabel(sys.technology)}</span>
                          <span className="text-mono-200 text-xs font-semibold">{formatCurrencyFull(sys.monthly_cost)}/mo</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-mono-600 text-xs pt-3 border-t border-mono-800">
                      No active systems declared
                    </p>
                  )}
                </div>
              ) : null}
            </div>

            {/* BUCKET 3: O&M / Monitoring (Operating) */}
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-5">
              <h4 className="text-xs font-semibold text-mono-500 tracking-wider mb-4">O&M</h4>
              {loadingBuckets ? (
                <div className="h-20 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : buckets ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-mono-100 text-2xl font-bold font-orbitron">
                      {formatCurrencyFull(buckets.om_monitoring.quarterly_total)}<span className="text-mono-500 text-sm font-normal">/qtr</span>
                    </p>
                    <p className="text-mono-600 text-[10px] tracking-wider mt-1">
                      {formatCurrencyFull(buckets.om_monitoring.annual_total)}/YR
                    </p>
                  </div>
                  <div className="pt-3 border-t border-mono-800 space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-mono-400 text-xs">Sampling ({wellCount} wells)</span>
                      <span className="text-mono-200 text-xs font-semibold">{formatCurrencyFull(buckets.om_monitoring.quarterly_sampling_cost)}/qtr</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-mono-400 text-xs">Quarterly Report</span>
                      <span className="text-mono-200 text-xs font-semibold">{formatCurrencyFull(buckets.om_monitoring.quarterly_report_cost)}/qtr</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-mono-400 text-xs">Mobilization</span>
                      <span className="text-mono-200 text-xs font-semibold">{formatCurrencyFull(buckets.om_monitoring.quarterly_mobilization)}/qtr</span>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* ── Summary Metrics ── */}
        {buckets && (
          <FadeIn delay={50}>
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
              <div className="grid grid-cols-4 gap-6">
                <div>
                  <p className="text-mono-600 text-xs tracking-wider mb-1">ANNUAL OPERATING</p>
                  <p className="text-mono-100 text-2xl font-bold font-orbitron">{formatCurrency(buckets.grand_total_annual)}</p>
                </div>
                <div>
                  <p className="text-mono-600 text-xs tracking-wider mb-1">CAPITAL BUDGET</p>
                  <p className="text-mono-100 text-2xl font-bold font-orbitron">{formatCurrency(buckets.grand_total_capital)}</p>
                </div>
                <div>
                  <p className="text-mono-600 text-xs tracking-wider mb-1">ACTIVE WELLS</p>
                  <p className="text-mono-100 text-2xl font-bold font-orbitron">{wellCount || '--'}</p>
                </div>
                <div>
                  <p className="text-mono-600 text-xs tracking-wider mb-1">MONTHLY BURN RATE</p>
                  <p className="text-mono-100 text-2xl font-bold font-orbitron">
                    {costSnapshot?.monthly_burn_rate != null
                      ? formatCurrency(costSnapshot.monthly_burn_rate)
                      : '--'}
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        )}

        {/* ── Remediation Systems Panel ── */}
        <FadeIn delay={100}>
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
            <RemediationSystemsPanel siteId={siteId} onSystemsChanged={() => loadBuckets(true)} />
          </div>
        </FadeIn>

        {/* ── Recommended Remediation: CURRENT hotspots ── */}
        {buckets?.recommended_remediation && buckets.recommended_remediation.hotspots.length > 0 && (
          <FadeIn delay={120}>
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xs font-semibold text-mono-300 tracking-wider">RECOMMENDED REMEDIATION · CURRENT</h4>
                <span className="text-[10px] text-mono-500">{buckets.recommended_remediation.hotspots.length} hotspot{buckets.recommended_remediation.hotspots.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="space-y-2">
                {buckets.recommended_remediation.hotspots.map(h => (
                  <div key={`now-${h.well_id}`} className="flex items-start justify-between bg-mono-850 rounded px-4 py-3">
                    <div className="min-w-0">
                      <span className="text-mono-100 text-sm font-semibold">{h.well_name}</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {h.types.includes('psh') && (
                          <span className="px-1.5 py-0.5 rounded text-xs tracking-wider bg-amber-900/40 text-amber-400 border border-amber-800">PSH</span>
                        )}
                        {h.types.includes('dissolved') && (
                          <span className="px-1.5 py-0.5 rounded text-xs tracking-wider bg-cyan-900/40 text-cyan-400 border border-cyan-800">DISSOLVED</span>
                        )}
                      </div>
                      {h.psh_thickness_ft != null && (
                        <p className="text-mono-200 text-xs mt-0.5">{h.psh_thickness_ft.toFixed(2)} ft product</p>
                      )}
                      {h.dissolved_compound && h.dissolved_max_conc_ugl != null && (
                        <p className="text-mono-200 text-xs mt-0.5">{h.dissolved_compound}: {(h.dissolved_max_conc_ugl / 1000).toFixed(3)} mg/L</p>
                      )}
                    </div>
                    <span className={`px-1.5 py-0.5 rounded text-[11px] tracking-wider shrink-0 ${
                      h.severity === 'critical' ? 'bg-red-900/40 text-red-400 border border-red-800'
                      : h.severity === 'high' ? 'bg-orange-900/40 text-orange-400 border border-orange-800'
                      : 'bg-mono-800 text-mono-400 border border-mono-700'
                    }`}>
                      {h.severity.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        )}

        {/* ── Recommended Remediation: v3.2 20-YEAR predicted hotspots ── */}
        {(() => {
          const futureHotspots = v32Cost
            ? Object.entries(v32Cost.hotspot_overlay)
                .filter(([, o]) => o.exceeds_at_max_horizon || o.worst_horizon_p_exceed >= 0.7)
                .map(([wellId, o]) => ({ wellId, ...o }))
            : [];
          if (futureHotspots.length === 0) return null;
          return (
            <FadeIn delay={140}>
              <div className="bg-mono-900 border border-red-800/40 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xs font-semibold text-red-300 tracking-wider">
                    RECOMMENDED REMEDIATION · {v32Cost?.liability_horizon_label?.toUpperCase() ?? '20YR'} PREDICTED
                  </h4>
                  <span className="text-[10px] text-mono-500">
                    {futureHotspots.length} well{futureHotspots.length !== 1 ? 's' : ''} · v3.2
                  </span>
                </div>
                <div className="space-y-2">
                  {futureHotspots.map(h => (
                    <div key={`future-${h.wellId}`} className="flex items-start justify-between bg-mono-850 rounded px-4 py-3">
                      <div className="min-w-0">
                        <span className="text-mono-100 text-sm font-semibold">{h.well_name ?? h.wellId.slice(0, 8)}</span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="px-1.5 py-0.5 rounded text-xs tracking-wider bg-red-900/40 text-red-300 border border-red-800">
                            {h.worst_tier_label.toUpperCase()}
                          </span>
                          <span className="text-mono-400 text-[10px]">
                            {h.worst_ratio.toFixed(1)}× MCL · clears @ {h.clear_year}yr
                          </span>
                        </div>
                        <p className="text-mono-200 text-xs mt-0.5">
                          P(exceed) = {(h.worst_horizon_p_exceed * 100).toFixed(0)}%
                        </p>
                      </div>
                      <span className="px-1.5 py-0.5 rounded text-[11px] tracking-wider shrink-0 bg-red-900/40 text-red-400 border border-red-800">
                        REMEDIATE NOW
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          );
        })()}

        {/* "Recommended Sentinel Wells" detail block removed. Bucket 1
            (New Well Installation) at the top already reports the count +
            cost-per-well + total. Per-well detail with priority / depth /
            install_cost lives on the Delineation Intelligence tab, which is
            the canonical surface for delineation recommendations. */}

        {/* ── Cost Escalation Timeline ── */}
        <FadeIn delay={200}>
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron">
                COST ESCALATION TIMELINE
              </h3>
              <div className="flex items-center gap-4">
                {v32Cost && (
                  <span className="text-[10px] text-cyan-400 bg-cyan-900/30 px-2 py-0.5 rounded tracking-wider">
                    V3.2 LIVE
                  </span>
                )}
                <div className="flex items-center gap-2 text-mono-500 text-xs">
                  <ClockIcon className="h-4 w-4" />
                  {v32Cost
                    ? `${v32Cost.curve.length} horizons (now → ${v32Cost.liability_horizon_label})`
                    : 'computing curve…'
                  }
                </div>
              </div>
            </div>

            {v32Cost && v32Cost.curve.length > 0 ? (
              <>
                <p className="text-mono-500 text-xs mb-3">
                  Cost if remediation is delayed to each v3.2 horizon. Each row shows total cost at that scenario start
                  and the additional cost vs. acting now. Wells column counts wells still exceeding MCL at that horizon.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-mono-800">
                        <th className="text-left text-mono-500 text-xs tracking-wider py-3 px-4">DELAY</th>
                        <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-4">WELLS HOT</th>
                        <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-4">MONITORING</th>
                        <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-4">TREATMENT</th>
                        <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-4">DELINEATION</th>
                        <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-4">TOTAL</th>
                        <th className="text-right text-mono-500 text-xs tracking-wider py-3 px-4">vs ACT NOW</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const startNow = v32Cost.curve.find(p => p.label === 'now')?.total_cost ?? 0;
                        return v32Cost.curve.map((p, idx) => {
                          const delineation = p.delineation_install_cost + p.delineation_monitor_cost;
                          const vsNow = p.total_cost - startNow;
                          return (
                            <tr key={idx} className="border-b border-mono-800 hover:bg-mono-850">
                              <td className="py-3 px-4">
                                <span className="text-mono-200 text-sm">{p.label === 'now' ? 'Start now' : `+ ${p.label}`}</span>
                              </td>
                              <td className="text-right py-3 px-4">
                                <span className="text-mono-300 text-sm">{p.wells_hot}</span>
                              </td>
                              <td className="text-right py-3 px-4">
                                <span className="text-mono-400 text-sm">{formatCurrency(p.monitoring_cost)}</span>
                              </td>
                              <td className="text-right py-3 px-4">
                                <span className="text-mono-400 text-sm">{formatCurrency(p.treatment_cost)}</span>
                              </td>
                              <td className="text-right py-3 px-4">
                                <span className="text-mono-400 text-sm">{formatCurrency(delineation)}</span>
                              </td>
                              <td className="text-right py-3 px-4">
                                <span className="text-mono-100 text-sm font-semibold">{formatCurrency(p.total_cost)}</span>
                              </td>
                              <td className="text-right py-3 px-4">
                                <span className={`text-sm font-semibold ${vsNow > 0 ? 'text-amber-400' : 'text-mono-600'}`}>
                                  {vsNow > 0 ? `+${formatCurrency(vsNow)}` : '--'}
                                </span>
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-mono-500 text-sm">v3.2 cost curve loading…</p>
                <p className="text-mono-600 text-xs mt-2">Curve appears once v3.2 predictions are available for this site.</p>
              </div>
            )}
          </div>
        </FadeIn>
      </div>
    </FadeIn>
  );
}
